import type { ViewStyle } from 'react-native';
import {
  breakpoints,
  layout,
  type FormFactor,
  type WebLayout,
} from '../constants/layout';
import { spacing } from '../constants/spacing';

export function formFactorForWidth(width: number): FormFactor {
  if (width >= breakpoints.desktop) return 'desktop';
  if (width >= breakpoints.laptop) return 'laptop';
  if (width >= breakpoints.tablet) return 'tablet';
  return 'mobile';
}

export function isCompactWidth(width: number): boolean {
  return width < breakpoints.tablet;
}

export function isWideWidth(width: number): boolean {
  return width >= breakpoints.laptop;
}

/**
 * Named max-width for the current viewport.
 * page / wide / fluid fill the viewport (desktop, laptop, tablet, and mobile).
 * form stays a narrow centered auth column.
 */
export function contentMaxWidthFor(
  webLayout: WebLayout,
  _width: number,
): number | undefined {
  if (webLayout === 'form') return layout.webFormMaxWidth;
  return undefined;
}

/**
 * Web-only content cap inside the sidebar shell. Native keeps contentMaxWidthFor
 * so mobile and tablet apps are unchanged.
 */
export function webContentMaxWidthFor(webLayout: WebLayout): number | undefined {
  if (webLayout === 'form') return layout.webFormMaxWidth;
  if (webLayout === 'wide') return layout.webWideContentMaxWidth;
  if (webLayout === 'narrow') return layout.webNarrowContentMaxWidth;
  if (webLayout === 'fluid') return undefined;
  return layout.webContentMaxWidth;
}

/** Web page gutter grows with the viewport; mobile web keeps the native gutter. */
export function webPageGutterFor(width: number): number {
  const factor = formFactorForWidth(width);
  if (factor === 'desktop') return spacing.xxl;
  return spacing.xl;
}

/** Web shell shows a sidebar instead of the bottom tab bar from tablet width up. */
export function webSidebarModeFor(width: number): 'none' | 'rail' | 'expanded' {
  if (width < breakpoints.tablet) return 'none';
  if (width < breakpoints.laptop) return 'rail';
  return 'expanded';
}

export function gridColumnsForWidth(width: number): number {
  const factor = formFactorForWidth(width);
  if (factor === 'desktop') return 4;
  if (factor === 'laptop') return 3;
  if (factor === 'tablet') return 2;
  return 1;
}

export function monthMetricColumnsForWidth(width: number): number {
  return isCompactWidth(width) ? 2 : 4;
}

/** Percentage width that leaves room for row gaps when using space-between. */
export function gridItemWidthPercent(columns: number): `${number}%` {
  if (columns <= 1) return '100%';
  if (columns === 2) return '48%';
  if (columns === 3) return '31%';
  return '23.5%';
}

export function overlayPaddingForWidth(width: number): number {
  return isCompactWidth(width) ? spacing.md : spacing.xl;
}

export function dialogCardStyle(width: number): ViewStyle {
  const compact = isCompactWidth(width);
  return {
    width: '100%',
    maxWidth: compact ? layout.webModalMaxWidth : layout.webDialogMaxWidth,
    height: compact ? undefined : '90%',
    maxHeight: compact ? '90%' : 820,
    borderRadius: 16,
    overflow: 'hidden',
    minWidth: 0,
  };
}

export function modalCardStyle(width: number): ViewStyle {
  return {
    width: '100%',
    maxWidth: Math.min(layout.webModalMaxWidth, Math.max(width - spacing.xl * 2, 0)),
    maxHeight: '90%',
    borderRadius: 16,
    minWidth: 0,
  };
}

export function drawerWidthFor(width: number): number {
  return Math.min(width * 0.86, layout.webDrawerMaxWidth);
}
